import React, { useEffect, useCallback, useReducer, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";
import AuthService from "services/AuthService";
import * as microsoftTeams from "@microsoft/teams-js";
import { useCredentialsContext } from "store/contexts/credentialsContext";
import "assets/scss/partials/authentication.scss";
import "assets/scss/base/custom.scss";
import { useGlobalContext } from "store/contexts/globalContext";
import logo from "assets/img/reteam_logo.png";
import CustomInput from "components/CustomInput/CustomInput";
import CustomButton from "components/CustomButton";
import Loader from "components/Loader";
import { Modal } from "react-responsive-modal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { initialState, reducer } from "store/reducers/registerReducer";
import {
  faUserTie,
  faEnvelope,
  faLock,
  faEye,
  faEyeSlash,
} from "@fortawesome/free-solid-svg-icons";

export default () => {
  const { setCredentials } = useCredentialsContext();
  const history = useHistory();
  const { microsoftConfigureTab } = useGlobalContext();
  const [registerStats, dispatch] = useReducer(reducer, initialState);
  const [trailLoading, setTrailLoading] = useState(false);
  const { loading, domainError, showPassword, showConfirmPassword, showError } =
    registerStats;

  const passwordStyle = {
    marginTop: "0.5rem",
  };

  useEffect(() => {
    microsoftTeams.app.initialize();
    microsoftTeams.app.notifySuccess();
    //eslint-disable-next-line
  }, []);

  const formik = useFormik({
    initialValues: {
      first_name: "",
      last_name: "",
      email: "",
      password: "",
      password_confirmation: "",
    },
    validateOnBlur: false,
    validationSchema: registrationValidationSchema(),
    onSubmit: async (values) => {
      try {
        dispatch({ type: "setLoading", value: true });
        await AuthService.register(values);

        setCredentials({
          email: values.email,
        });

        history.replace("/verify-account");
      } catch (error) {
        if (error.response.data.status === 412) {
          dispatch({
            type: "setDomainError",
            value: error.response.data.message,
          });
        }

        if (error.response.data.status !== 500) {
          setErrorState(error.response.data.errors);
        }
      }
      dispatch({ type: "setLoading", value: false });
    },
  });

  const setErrorState = (message) => {
    dispatch({ type: "setShowError", value: message });
    setTimeout((_) => {
      dispatch({ type: "setShowError", value: null });
    }, 4000);
  };

  const closeModal = useCallback(() => {
    dispatch({
      type: "setDomainError",
      value: null,
    });
  }, []);

  const requestTrial = async () => {
    try {
      setTrailLoading(true);
      await AuthService.register({ ...formik.values, request_access: true });
      closeModal();
      setTrailLoading(false);

      setCredentials({
        email: formik.values.email,
      });

      history.replace("/verify-account");
    } catch (e) {
      console.log(e);
    }
  };

  return (
    <>
      <Modal
        classNames={{
          modalContainer: "modalContainer",
          modal: "rootModalContainer",
        }}
        open={domainError}
        showCloseIcon={false}
        onClose={closeModal}
      >
        <div className="alertModal">
          <div className="alertHeading">Get your company registered</div>
          <span className="alertMessage">
            Your company is currently not registered with ReTeam. Do you want to
            request for access?
          </span>
          <div className="alertFooter">
            <CustomButton
              disabled={trailLoading && true}
              buttonClass="small-btn me-2"
              onClick={closeModal}
            >
              Cancel
            </CustomButton>
            <CustomButton
              disabled={trailLoading && true}
              buttonClass="small-btn me-2"
              onClick={requestTrial}
            >
              {!trailLoading ? "Yes" : <Loader width={10} height={10} />}
            </CustomButton>
          </div>
        </div>
      </Modal>
      <div className="root" id="user_login">
        <div className="inputBox">
          <div className="login_logo textcenter">
            <img className="logo" alt="ReTeam Logo" src={logo} />
          </div>
          <div className="content">
            <div className="textcenter heading">
              Register To Manage Your Account
            </div>
            <div className="formContent">
              <form noValidate onSubmit={formik.handleSubmit}>
                <div className="inputFields">
                  <CustomInput
                    id={"first-name"}
                    startIcon={
                      <FontAwesomeIcon icon={faUserTie} className="formIcon" />
                    }
                    inputProps={{
                      type: "text",
                      placeholder: "First Name",
                      ...formik.getFieldProps("first_name"),
                      error:
                        !!formik.errors.first_name &&
                        !!formik.touched.first_name
                          ? formik.errors.first_name
                          : null,
                    }}
                  />
                  <CustomInput
                    id={"last-name"}
                    startIcon={
                      <FontAwesomeIcon icon={faUserTie} className="formIcon" />
                    }
                    inputProps={{
                      type: "text",
                      placeholder: "Last Name",
                      ...formik.getFieldProps("last_name"),
                      error:
                        !!formik.errors.last_name && !!formik.touched.last_name
                          ? formik.errors.last_name
                          : null,
                    }}
                  />
                  <CustomInput
                    id={"email"}
                    startIcon={
                      <FontAwesomeIcon icon={faEnvelope} className="formIcon" />
                    }
                    inputProps={{
                      type: "email",
                      placeholder: "Your Email",
                      ...formik.getFieldProps("email"),
                      error:
                        !!formik.errors.email && !!formik.touched.email
                          ? formik.errors.email
                          : null,
                    }}
                  />
                  {showError !== null && (
                    <div className="errorMessage">{showError?.email[0]}</div>
                  )}
                  <CustomInput
                    id={"password"}
                    startIcon={
                      <FontAwesomeIcon icon={faLock} className="formIcon" />
                    }
                    inputProps={{
                      type: showPassword ? "text" : "password",
                      placeholder: "Create Password",
                      ...formik.getFieldProps("password"),
                      error:
                        !!formik.errors.password && !!formik.touched.password
                          ? formik.errors.password
                          : null,
                    }}
                    style={passwordStyle}
                    endIcon={
                      <FontAwesomeIcon
                        onClick={() =>
                          dispatch({
                            type: "setShowPassword",
                            value: !showPassword,
                          })
                        }
                        aria-label="toggle password visibility"
                        icon={!showPassword ? faEyeSlash : faEye}
                        id="togglePassword"
                        style={{ marginLeft: "-30px", cursor: "pointer" }}
                      />
                    }
                  />
                  <CustomInput
                    id={"confirm-pwd"}
                    startIcon={
                      <FontAwesomeIcon icon={faLock} className="formIcon" />
                    }
                    inputProps={{
                      type: showConfirmPassword ? "text" : "password",
                      placeholder: "Confirm Password",
                      ...formik.getFieldProps("password_confirmation"),
                      error:
                        !!formik.errors.password_confirmation &&
                        !!formik.touched.password_confirmation
                          ? formik.errors.password_confirmation
                          : null,
                    }}
                    style={passwordStyle}
                    endIcon={
                      <FontAwesomeIcon
                        onClick={() =>
                          dispatch({
                            type: "setShowConfirmPassword",
                            value: !showConfirmPassword,
                          })
                        }
                        icon={!showConfirmPassword ? faEyeSlash : faEye}
                        style={{ marginLeft: "-30px", cursor: "pointer" }}
                      />
                    }
                  />
                </div>
                {!microsoftConfigureTab && (
                  <div className="checkbox">
                    <label className="color-light">
                      By continuing, You agree with ReTeam's{" "}
                      <a
                        href="https://beremote.com/terms-of-use"
                        style={{
                          color: "#575F6B",
                          textDecoration: "underline",
                        }}
                      >
                        Terms of Use
                      </a>{" "}
                      &amp;{" "}
                      <a
                        href="https://beremote.com/privacy-policy"
                        style={{
                          color: "#575F6B",
                          textDecoration: "underline",
                        }}
                      >
                        Privacy Policy
                      </a>
                      .
                    </label>
                  </div>
                )}
                <CustomButton
                  name={
                    !loading ? (
                      "Register Now"
                    ) : (
                      <Loader width={40} height={40} />
                    )
                  }
                  disabled={loading && true}
                />
              </form>
              <div className="textcenter">
                Already have an account? <Link to="/login">Login.</Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const registrationValidationSchema = () => {
  return Yup.object().shape({
    first_name: Yup.string()
      .matches(
        /^[a-zA-Z\s]+$/,
        "First name cannot contain special characters and numbers "
      )
      .min(2, "Name must be between 2 to 50 characters.")
      .max(50, "Name must be between 2 to 50 characters")
      .required("First Name is required."),
    last_name: Yup.string()
      .matches(
        /^[a-zA-Z\s]+$/,
        "Last name cannot contain special characters and numbers "
      )
      .min(2, "Name must be between 2 to 50 characters.")
      .max(50, "Name must be between 2 to 50 characters")
      .required("Last Name is required."),
    email: Yup.string().email().required("Email is required."),
    password: Yup.string()
      .matches(
        /^(?=.*[0-9])(?=.*[!=%^~():/<>;{}|_+@#$%^&*.,])(?=.*[a-zA-Z])/,
        "password must contain 8-20 characters including letters, numbers & special characters."
      )
      .min(8, "Password cannot be less then 8 words")
      .max(20, "Password cannot be greater than 20 words")
      .required("Password is required"),
    password_confirmation: Yup.string()
      .equals([Yup.ref("password")], "Passwords Must Match")
      .required("Password Confirmation is requried."),
  });
};
